// ─────────────────────────────────────────
//  AI REQUEST LAYER
// ─────────────────────────────────────────

import { S, sb } from './state.js';
import { sleep, showToast, extractCode } from './utils.js';
import { addMsg, removeTyping, createStreamBubble, renderStreamText, addApplyButtons } from './ui.js';

/*── AI Model routing ──*/
export const MODELS = {
  SONNET: 'claude-sonnet-5-20260514',       // Complex new builds — latest generation
  HAIKU:  'claude-haiku-4-5-20251001',      // Everything else — fast + cheap
};

export async function aiRequest(systemPrompt, messages, model = MODELS.HAIKU) {
  const useProxy = !S.key;
  const url = useProxy ? '/api/ai' : 'https://api.anthropic.com/v1/messages';
  const headers = { 'Content-Type': 'application/json' };
  if (useProxy && S.session) {
    headers['Authorization'] = 'Bearer ' + S.session.access_token;
  } else if (!useProxy) {
    headers['x-api-key'] = S.key;
    headers['anthropic-version'] = '2023-06-01';
    headers['anthropic-dangerous-direct-browser-access'] = 'true';
  }

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      model,
      max_tokens: model === MODELS.HAIKU ? 6000 : 12000,
      system: systemPrompt,
      messages,
    })
  });
  let data;
  try {
    data = await res.json();
  } catch {
    // Response isn't JSON (e.g. 504 timeout returns HTML)
    removeTyping();
    const errMsg = res.status === 504 ? 'AI server timed out. Try again.' :
                   res.status === 502 ? 'AI server temporarily unavailable.' :
                   'AI request failed (status ' + res.status + ')';
    addMsg('assistant', '⚠️ ' + errMsg);
    setStatus('');
    return null;
  }

  if (!res.ok || data.error) {
    removeTyping();
    const errMsg = data.error?.message || data.error || 'AI request failed';
    addMsg('assistant', '⚠️ ' + errMsg);
    setStatus('');
    return null;
  }

  return data.content?.[0]?.text || '';
}

// ── Silent streaming — collects response without UI (for background steps) ──
export async function aiRequestSilent(systemPrompt, messages, model = MODELS.HAIKU) {
  const useProxy = !S.key;
  const url = useProxy ? '/api/ai' : 'https://api.anthropic.com/v1/messages';
  const headers = { 'Content-Type': 'application/json' };
  if (useProxy && S.session) {
    headers['Authorization'] = 'Bearer ' + S.session.access_token;
  } else if (!useProxy) {
    headers['x-api-key'] = S.key;
    headers['anthropic-version'] = '2023-06-01';
    headers['anthropic-dangerous-direct-browser-access'] = 'true';
  }

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model,
        max_tokens: model === MODELS.HAIKU ? 6000 : 12000,
        stream: true,
        system: systemPrompt,
        messages,
      })
    });
  } catch (err) {
    return null;
  }

  if (!res.ok) return null;

  // Check if response is JSON (non-streaming fallback)
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const data = await res.json();
      return data.content?.[0]?.text || '';
    } catch { return null; }
  }

  // Parse SSE stream silently
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const d = line.slice(6).trim();
        if (d === '[DONE]') continue;
        try {
          const event = JSON.parse(d);
          if (event.type === 'content_block_delta' && event.delta?.text) {
            fullText += event.delta.text;
          }
        } catch (_) {}
      }
    }
  } catch (err) {
    if (!fullText) return null;
  }

  return fullText || null;
}

// ── Streaming AI request — renders tokens live into a chat bubble ──
// Uses persistent build endpoint: streams in real-time but saves result server-side
// If client disconnects (phone sleep, call), result is recoverable via polling
export async function aiRequestStream(systemPrompt, messages, model = MODELS.HAIKU) {
  const useProxy = !S.key;

  // BYOK users go direct to Anthropic (no persistence needed — they control their own key)
  if (!useProxy) {
    return _directStream(systemPrompt, messages, model);
  }

  // Platform users use the persistent build endpoint
  const url = '/api/ai/build';
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + S.session.access_token,
  };

  removeTyping();
  const { bubble, wrap } = createStreamBubble();

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model,
        max_tokens: model === MODELS.HAIKU ? 6000 : 12000,
        system: systemPrompt,
        messages,
      })
    });
  } catch (err) {
    bubble.textContent = '⚠️ Connection failed. Check your internet.';
    setStatus('');
    return null;
  }

  if (!res.ok) {
    try {
      const errData = await res.json();
      bubble.textContent = '⚠️ ' + (errData.error?.message || errData.error || 'Error ' + res.status);
    } catch {
      bubble.textContent = '⚠️ Error ' + res.status;
    }
    setStatus('');
    return null;
  }

  // Parse the SSE stream
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let buffer = '';
  let jobId = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') continue;

        try {
          const event = JSON.parse(data);

          // Capture jobId from first event
          if (event.type === 'job_created' && event.jobId) {
            jobId = event.jobId;
            S._activeJobId = jobId;
            continue;
          }

          if (event.type === 'content_block_delta' && event.delta?.text) {
            fullText += event.delta.text;
            renderStreamText(bubble, fullText);
          }

          if (event.type === 'error') {
            bubble.textContent = '⚠️ AI Error: ' + (event.error?.message || 'Stream error');
            S._activeJobId = null;
            return null;
          }
        } catch (_) {}
      }
    }
  } catch (err) {
    // Stream interrupted (phone sleep, network drop, etc.)
    if (!fullText && jobId) {
      // We have a jobId — poll for result
      bubble.textContent = '⏳ Connection lost. Recovering build...';
      const recovered = await _pollForResult(jobId);
      if (recovered) {
        fullText = recovered;
        bubble.textContent = fullText;
        if (extractCode(fullText)) addApplyButtons(wrap, fullText);
        S._activeJobId = null;
        return fullText;
      } else {
        bubble.textContent = '⚠️ Build interrupted. Try again.';
        S._activeJobId = null;
        return null;
      }
    }
    if (!fullText) {
      bubble.textContent = '⚠️ Stream interrupted. Try again.';
      S._activeJobId = null;
      return null;
    }
  }

  // Final render
  if (fullText) {
    bubble.textContent = fullText;
    if (extractCode(fullText)) addApplyButtons(wrap, fullText);
  }

  S._activeJobId = null;
  const msgs = document.getElementById('messages');
  msgs.scrollTop = msgs.scrollHeight;
  return fullText;
}

// ── Poll for build result (used after disconnect/reconnect) ──
async function _pollForResult(jobId, maxAttempts = 30) {
  for (let i = 0; i < maxAttempts; i++) {
    await sleep(2000); // Poll every 2 seconds

    try {
      const res = await fetch('/api/ai/build/' + jobId, {
        headers: { 'Authorization': 'Bearer ' + S.session.access_token },
      });
      if (!res.ok) return null;

      const data = await res.json();
      if (data.status === 'completed' && data.result) {
        return data.result;
      }
      if (data.status === 'failed') {
        return null;
      }
      // Still streaming — keep polling
    } catch {
      // Network still down — keep trying
    }
  }
  return null; // Timed out after 60 seconds
}

// ── Check for pending builds on app resume (visibility change) ──
export function checkPendingBuild() {
  if (!S._activeJobId || !S.session) return;

  const jobId = S._activeJobId;
  fetch('/api/ai/build/' + jobId, {
    headers: { 'Authorization': 'Bearer ' + S.session.access_token },
  })
    .then(res => res.json())
    .then(data => {
      if (data.status === 'completed' && data.result) {
        S._activeJobId = null;
        // Apply the result
        const code = extractCode(data.result);
        if (code) {
          import('./editor.js').then(({ applyCode, notifyCodeUpdate }) => {
            applyCode(code);
            notifyCodeUpdate();
          });
          import('./preview.js').then(({ runCode }) => runCode());
          showToast('✓ Build completed while you were away!');
          addMsg('assistant', data.result, true);
        }
      }
    })
    .catch(() => {}); // Silently fail — will retry on next visibility change
}

// ── Direct streaming for BYOK users (no persistence needed) ──
async function _directStream(systemPrompt, messages, model) {
  const url = 'https://api.anthropic.com/v1/messages';
  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': S.key,
    'anthropic-version': '2023-06-01',
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  removeTyping();
  const { bubble, wrap } = createStreamBubble();

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model,
        max_tokens: model === MODELS.HAIKU ? 6000 : 12000,
        stream: true,
        system: systemPrompt,
        messages,
      })
    });
  } catch (err) {
    bubble.textContent = '⚠️ Connection failed. Check your internet.';
    setStatus('');
    return null;
  }

  if (!res.ok) {
    try {
      const text = await res.text();
      const err = JSON.parse(text);
      bubble.textContent = '⚠️ ' + (err.error?.message || 'Error ' + res.status);
    } catch {
      bubble.textContent = '⚠️ Error ' + res.status;
    }
    setStatus('');
    return null;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let fullText = '';
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (data === '[DONE]') continue;
        try {
          const event = JSON.parse(data);
          if (event.type === 'content_block_delta' && event.delta?.text) {
            fullText += event.delta.text;
            renderStreamText(bubble, fullText);
          }
        } catch (_) {}
      }
    }
  } catch (err) {
    if (!fullText) {
      bubble.textContent = '⚠️ Stream interrupted. Try again.';
      return null;
    }
  }

  if (fullText) {
    bubble.textContent = fullText;
    if (extractCode(fullText)) addApplyButtons(wrap, fullText);
  }

  const msgs = document.getElementById('messages');
  msgs.scrollTop = msgs.scrollHeight;
  return fullText;
}

// ── Helper: update status pill (local to this module) ──
function setStatus(text) {
  const el = document.getElementById('status-txt');
  if (el) el.textContent = text;
}
